import * as React from "react";
import { useNavigate } from "react-router-dom";
import FormControl from "react-bootstrap/FormControl";
import Table from "react-bootstrap/Table";
import { useApolloClient } from "@apollo/client";
import PageHeader from "theme/components/common/PageHeader";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Pagination,
  Row,
  Tab,
  Tabs,
} from "react-bootstrap";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import apiClient from "../api";
import { useAuth } from "../state/auth";
import {
  USER_ROLE_OPTIONS,
  loadManagedOrganizations,
  loadEditableUsersPage,
  loadUserChoices,
  ManagedOrganizationOption,
  EditableUsersPage,
  EditableUsersQuery,
  UserChoice,
  PasswordState,
  PASSWORD_STATE_LABELS,
  PasswordStateBadge,
  MfaState,
  MFA_STATE_LABELS,
  MfaStateBadge,
} from "utils/users";
import { gettext, getLocaleDropdownOptions } from "i18n";
import { getFormValueHandler } from "utils/forms";
import { sortByTranslatedKey } from "utils/arrays";
import { timestampToDatetimeString } from "utils/formatting";

import "./UserManagementPage.scss";

interface UserListPagerProps {
  page: number;
  pageCount: number;
  setPage: (page: number) => void;
}
const UserListPager: React.FC<UserListPagerProps> = ({
  page,
  pageCount,
  setPage,
}: UserListPagerProps) => (
  <Pagination className="justify-content-center text-nowrap mb-0">
    <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page <= 1}>
      {gettext("Previous")}
    </Pagination.Prev>
    <Pagination.Item disabled>
      {gettext("Page %1 of %2", String(page), String(pageCount))}
    </Pagination.Item>
    <Pagination.Next
      onClick={() => setPage(page + 1)}
      disabled={page >= pageCount}
    >
      {gettext("Next")}
    </Pagination.Next>
  </Pagination>
);

interface UserListTabProps {
  // Changed to load the list again, e.g. after a user was added
  reloadCount?: number;
}
/**
 * The users the current user may edit, a page at a time: the server searches
 * and filters them, there being too many to load at once
 */
const UserListTab: React.FC<UserListTabProps> = ({
  reloadCount = 0,
}: UserListTabProps) => {
  const navigate = useNavigate();
  const listRef = React.useRef<HTMLDivElement>(null);
  const [search, setSearch] = React.useState<string>("");
  // One state, so that a new search or filter and going back to the first
  // page load one page, not two. The search is the one typed once the user
  // stops typing.
  const [query, setQuery] = React.useState<
    Required<Omit<EditableUsersQuery, "perPage">>
  >({
    page: 1,
    search: "",
    passwordState: "",
    mfaState: "",
  });
  const { page, passwordState, mfaState } = query;
  const [result, setResult] = React.useState<EditableUsersPage>();

  const filter = (changes: Partial<EditableUsersQuery>) =>
    setQuery((current) => ({ ...current, ...changes, page: 1 }));
  const setPage = (newPage: number) =>
    setQuery((current) => ({ ...current, page: newPage }));

  const searchLater = React.useMemo(
    () =>
      debounce(
        (value: string) =>
          setQuery((current) => ({ ...current, search: value, page: 1 })),
        400,
      ),
    [],
  );
  React.useEffect(() => () => searchLater.cancel(), [searchLater]);

  React.useEffect(() => {
    let cancelled = false;
    loadEditableUsersPage(query)
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        // The page no longer exists, e.g. after users were deleted
        const pageCount = Math.ceil(loaded.total / loaded.per_page);
        if (!loaded.users.length && query.page > 1 && pageCount) {
          setQuery((current) => ({ ...current, page: pageCount }));
          return;
        }
        setResult(loaded);
      })
      .catch((e) => {
        console.error(e);
        // A request for a page or search no longer shown no longer matters
        if (!cancelled) {
          toast.error(gettext("Failed to load users"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [query, reloadCount]);

  const changePage = (newPage: number) => {
    setPage(newPage);
    // On a phone, the pager is at the end of a long list
    listRef.current?.scrollIntoView?.({ block: "start" });
  };

  const filtered = !!(query.search.trim() || passwordState || mfaState);
  const pageCount = result ? Math.ceil(result.total / result.per_page) : 0;

  return (
    <div ref={listRef}>
      <PageHeader title="Existing Users" className="mb-3" />
      <Card>
        <Card.Body>
          <FormControl
            className="mb-3"
            value={search}
            placeholder={gettext("Search by name, email or organization")}
            aria-label={gettext("Search by name, email or organization")}
            onChange={(e: any) => {
              setSearch(e.target.value);
              searchLater(e.target.value);
            }}
          />
          <Row className="g-3 mb-3">
            <Col sm={6}>
              <Form.Select
                aria-label={gettext("Password")}
                value={passwordState}
                onChange={(e) =>
                  filter({
                    passwordState: e.target.value as PasswordState | "",
                  })
                }
              >
                <option value="">{gettext("All password states")}</option>
                {Object.entries(PASSWORD_STATE_LABELS).map(([key, label]) => (
                  <option key={`password_state_${key}`} value={key}>
                    {gettext(label)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col sm={6}>
              <Form.Select
                aria-label={gettext("Multi-factor authentication")}
                value={mfaState}
                onChange={(e) =>
                  filter({ mfaState: e.target.value as MfaState | "" })
                }
              >
                <option value="">{gettext("All MFA states")}</option>
                {Object.entries(MFA_STATE_LABELS).map(([key, label]) => (
                  <option key={`mfa_state_${key}`} value={key}>
                    {gettext(label)}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          {!result ? (
            <div>{gettext("Loading...")}</div>
          ) : !result.users.length ? (
            <div>
              {filtered
                ? gettext("No matching users")
                : gettext("No data available")}
            </div>
          ) : (
            <>
              <Table responsive className="stackedTable">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Display Name</th>
                    <th>Email</th>
                    <th>{gettext("Organizations")}</th>
                    <th>{gettext("Created")}</th>
                    <th>{gettext("Password")}</th>
                    <th>{gettext("MFA")}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.users.map((user) => (
                    <tr
                      key={`user_row_${user.id}`}
                      className="activeRow"
                      onClick={() => {
                        navigate(`/user/${user.id}/edit`);
                      }}
                    >
                      <td data-label="ID">{user.id}</td>
                      <td data-label={gettext("Display Name")}>
                        {user.display_name}
                      </td>
                      <td data-label={gettext("Email")} className="text-break">
                        {user.email}
                      </td>
                      <td data-label={gettext("Organizations")}>
                        {(user.organizations ?? [])
                          .map((organization) => organization.name)
                          .join(", ")}
                      </td>
                      <td data-label={gettext("Created")}>
                        {user.created_at
                          ? timestampToDatetimeString(user.created_at)
                          : ""}
                      </td>
                      <td data-label={gettext("Password")}>
                        <PasswordStateBadge user={user} />
                      </td>
                      <td data-label={gettext("MFA")}>
                        <MfaStateBadge user={user} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {pageCount > 1 && (
                <UserListPager
                  page={page}
                  pageCount={pageCount}
                  setPage={changePage}
                />
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

interface UserAddTabProps {
  managedOrganizations: ManagedOrganizationOption[];
  userRefetch: () => void;
  permissions?: string[];
}
const UserAddTab: React.FC<UserAddTabProps> = ({
  userRefetch,
  managedOrganizations,
  permissions = [],
}: UserAddTabProps) => {
  const { user } = useAuth();
  const [validated, setValidated] = React.useState<boolean>(false);
  const [editedValues, setEditedValues] = React.useState<Record<string, any>>(
    {},
  );
  const apolloClient = useApolloClient();
  const setValueHandler = getFormValueHandler(editedValues, setEditedValues);

  const setValue = (key: string, value: any) => {
    setEditedValues({
      ...editedValues,
      [key]: value,
    });
  };

  const submitDisabled = false;
  const onSave = async (e: any) => {
    e.preventDefault();
    if (submitDisabled) {
      return;
    }
    const config: Record<string, any> = {};
    const values = { ...editedValues };
    apiClient
      .request("/auth/create-user-record", {
        method: "POST",
        data: {
          ...values,
          config,
        },
      })
      .then(async () => {
        toast.success("User created successfully");
        setEditedValues({});
        setValidated(false);
        await userRefetch();
        // If this was an existing user updated by an org admin, reset store to refresh their org memberships
        await apolloClient.resetStore();
      })
      .catch((e) => {
        console.error(e);
        toast.error("Failed to create new user! " + e.toString());
      });
  };
  const handleSubmit = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    // Show any potential input errors
    setValidated(true);

    // If the form is valid, submit
    const form = event.currentTarget;
    if (form.checkValidity()) {
      onSave(event);
    }
  };
  if (!user) {
    return <div>Not logged in</div>;
  }

  return (
    <div>
      <PageHeader title="Add New User" className="mb-3" />
      <Card>
        <Card.Body>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            {/* Labels above the fields on a phone, beside them on wider screens */}
            <Form.Group as={Row} className="mb-3" controlId="new-user-email">
              <Form.Label column sm={4} lg={3}>
                Email
              </Form.Label>
              <Col sm={8} lg={9}>
                <FormControl
                  type="email"
                  value={editedValues.email ?? ""}
                  placeholder={"user@example.com"}
                  onChange={setValueHandler("email")}
                  required
                />
              </Col>
            </Form.Group>
            <Form.Group
              as={Row}
              className="mb-3"
              controlId="new-user-display-name"
            >
              <Form.Label column sm={4} lg={3}>
                Display Name
              </Form.Label>
              <Col sm={8} lg={9}>
                <FormControl
                  value={editedValues.displayName ?? ""}
                  placeholder={gettext("Display Name")}
                  onChange={setValueHandler("displayName")}
                  required
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4} lg={3}>
                Language
              </Form.Label>
              <Col sm={8} lg={9}>
                <Form.Select
                  required
                  aria-label="Selected language"
                  onChange={setValueHandler("locale")}
                  value={editedValues?.locale ?? ""}
                >
                  <option value="">Select language</option>
                  {Object.entries(getLocaleDropdownOptions()).map(
                    ([key, label]) => (
                      <option key={`role_${key}`} value={key}>
                        {gettext(label)}
                      </option>
                    ),
                  )}
                </Form.Select>
              </Col>
            </Form.Group>
            {permissions.includes("modify_admin") && (
              <Row className="mb-3">
                <Col sm={{ span: 8, offset: 4 }} lg={{ span: 9, offset: 3 }}>
                  <Form.Check
                    type="checkbox"
                    id="new-user-global-admin"
                    label="Global Admin"
                    onChange={(e: any) =>
                      setValue("globalAdmin", e.target.checked)
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="new-user-can-edit-formulas"
                    label="Can Edit Formulas"
                    onChange={(e: any) =>
                      setValue("canEditFormulas", e.target.checked)
                    }
                  />
                </Col>
              </Row>
            )}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4} lg={3}>
                Organization
              </Form.Label>
              <Col sm={8} lg={9}>
                <Form.Select
                  aria-label="Organization of the new user"
                  onChange={setValueHandler("organization")}
                  value={editedValues.organization ?? ""}
                >
                  <option></option>
                  {sortByTranslatedKey(managedOrganizations, "name").map(
                    (organization) => (
                      <option
                        key={`org_${organization.id}`}
                        value={organization.id}
                      >
                        {organization.name}
                      </option>
                    ),
                  )}
                </Form.Select>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4} lg={3}>
                Organization Role
              </Form.Label>
              <Col sm={8} lg={9}>
                <Form.Select
                  aria-label="Organization role of the new user"
                  onChange={setValueHandler("role")}
                  value={editedValues.role ?? ""}
                  required={!!editedValues.organization}
                >
                  <option></option>
                  {Object.entries(USER_ROLE_OPTIONS).map(([key, label]) => (
                    <option key={`role_${key}`} value={key}>
                      {gettext(label)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>
            <Row>
              <Col
                sm={{ span: 8, offset: 4 }}
                lg={{ span: 9, offset: 3 }}
                className="d-grid d-sm-block"
              >
                <Button type="submit" disabled={submitDisabled}>
                  Add
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

interface UserIPAddress {
  user_id: number;
  email: string;
  display_name: string;
  ip_address: string;
  first_seen_at: string;
  last_seen_at: string;
  // null when not known (recorded before tokens were)
  token_id: number | null;
  token_is_session: boolean | null;
  token_description: string | null;
  token_exists: boolean;
  token_suspended: boolean;
}

interface AdminAuthToken {
  id: number;
  description: string;
  is_session: boolean;
  expire_timestamp: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  suspended_at: string | null;
  suspended_by: string | null;
  organizations: { id: number; name: string }[];
}

const setAuthTokensSuspended = (
  userId: number,
  tokenIds: number[],
  suspended: boolean,
) =>
  apiClient.request("/admin/suspend-auth-tokens", {
    method: "POST",
    data: { user_id: userId, token_ids: tokenIds, suspended },
  });

const tokenLabel = (
  isSession: boolean | null,
  description: string | null,
  id: number,
) => {
  if (isSession) {
    return gettext("Web login");
  }
  return `${gettext("API token")}: ${description || `#${id}`}`;
};

const TokenStateBadges: React.FC<{ exists: boolean; suspended: boolean }> = ({
  exists,
  suspended,
}) => (
  <>
    {!exists && (
      <Badge bg="secondary" className="ms-1">
        {gettext("Deleted")}
      </Badge>
    )}
    {exists && suspended && (
      <Badge bg="danger" className="ms-1">
        {gettext("Suspended")}
      </Badge>
    )}
  </>
);

// Addresses first seen this recently are marked as new
const NEW_IP_ADDRESS_DAYS = 7;
const IP_ADDRESS_PERIOD_OPTIONS: Record<number, string> = {
  7: "Last 7 days",
  30: "Last 30 days",
  90: "Last 90 days",
  395: "Last 13 months",
};

const isNewIPAddress = (address: UserIPAddress) =>
  Date.now() - new Date(address.first_seen_at).getTime() <
  NEW_IP_ADDRESS_DAYS * 24 * 60 * 60 * 1000;

/**
 * Addresses users' logins and API tokens were used from, for global admins to
 * spot unusual logins
 */
interface UserIPAddressesTabProps {
  onShowTokens: (userId: number) => void;
  onShowActivity: (filter: ActivityFilter) => void;
}
const UserIPAddressesTab: React.FC<UserIPAddressesTabProps> = ({
  onShowTokens,
  onShowActivity,
}: UserIPAddressesTabProps) => {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState<boolean>(false);
  const [days, setDays] = React.useState<number>(30);
  const [search, setSearch] = React.useState<string>("");
  const [addresses, setAddresses] = React.useState<UserIPAddress[]>();
  const [truncated, setTruncated] = React.useState<boolean>(false);

  React.useEffect(() => {
    let cancelled = false;
    setAddresses(undefined);
    apiClient
      .request(`/admin/user-ip-addresses?days=${days}`, { method: "GET" })
      .then((result) => {
        if (!cancelled) {
          setAddresses(result.addresses as UserIPAddress[]);
          setTruncated(!!result.truncated);
        }
      })
      .catch((e) => {
        console.error(e);
        // A request for a period no longer chosen, or after the tab was closed, no longer matters
        if (!cancelled) {
          toast.error(gettext("Failed to load IP addresses"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const toggleSuspended = async (address: UserIPAddress) => {
    const tokenId = address.token_id;
    if (busy || !tokenId) {
      return;
    }
    const suspended = !address.token_suspended;
    setBusy(true);
    try {
      await setAuthTokensSuspended(address.user_id, [tokenId], suspended);
      // The same token may appear on several addresses
      setAddresses((current) =>
        current?.map((row) =>
          row.user_id === address.user_id && row.token_id === tokenId
            ? { ...row, token_suspended: suspended }
            : row,
        ),
      );
      toast.success(
        suspended ? gettext("Token suspended") : gettext("Token resumed"),
      );
    } catch (e) {
      toast.error(gettext((e as any).message.toString()));
    }
    setBusy(false);
  };

  const query = search.trim().toLowerCase();
  const shownAddresses = (addresses ?? []).filter(
    (address) =>
      !query ||
      address.email.toLowerCase().includes(query) ||
      address.display_name.toLowerCase().includes(query) ||
      address.ip_address.includes(query) ||
      (address.token_description ?? "").toLowerCase().includes(query),
  );

  return (
    <div>
      <PageHeader title={gettext("Login IP addresses")} className="mb-3" />
      <Card>
        <Card.Body>
          <p>
            {gettext(
              "Addresses that each user's logins and access tokens were used from. An address first seen in the last 7 days is marked as new.",
            )}{" "}
            {gettext(
              "A suspended token stops working within 5 minutes and can be resumed at any time.",
            )}
          </p>
          <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
            <Form.Select
              aria-label={gettext("Period")}
              value={days}
              onChange={(e: any) => setDays(Number(e.target.value))}
              className="periodSelect"
            >
              {Object.entries(IP_ADDRESS_PERIOD_OPTIONS).map(([key, label]) => (
                <option key={`ip_period_${key}`} value={key}>
                  {gettext(label)}
                </option>
              ))}
            </Form.Select>
            <FormControl
              value={search}
              placeholder={gettext("Search by user or IP address")}
              onChange={(e: any) => setSearch(e.target.value)}
            />
          </div>
          {truncated && (
            <p className="text-warning">
              {gettext(
                "Only the most recently seen addresses are shown. Choose a shorter period or search to narrow the list.",
              )}
            </p>
          )}
          {addresses === undefined ? null : !shownAddresses.length ? (
            <div>{gettext("No data available")}</div>
          ) : (
            <Table responsive className="stackedTable">
              <thead>
                <tr>
                  <th>{gettext("User")}</th>
                  <th>{gettext("IP address")}</th>
                  <th>{gettext("Authentication")}</th>
                  <th>{gettext("First seen")}</th>
                  <th>{gettext("Last seen")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {shownAddresses.map((address) => (
                  <tr
                    key={`ip_${address.user_id}_${address.ip_address}_${address.token_id}`}
                    className="activeRow"
                    onClick={() => navigate(`/user/${address.user_id}/edit`)}
                  >
                    <td data-label={gettext("User")} className="text-break">
                      {address.display_name || address.email}
                      {address.display_name && (
                        <div className="text-muted small">{address.email}</div>
                      )}
                    </td>
                    <td data-label={gettext("IP address")}>
                      {address.ip_address}{" "}
                      {isNewIPAddress(address) && (
                        <Badge bg="warning" text="dark">
                          {gettext("New")}
                        </Badge>
                      )}
                    </td>
                    <td data-label={gettext("Authentication")}>
                      {!address.token_id ? (
                        <span className="text-muted">{gettext("Unknown")}</span>
                      ) : (
                        <>
                          {tokenLabel(
                            address.token_is_session,
                            address.token_description,
                            address.token_id,
                          )}
                          <TokenStateBadges
                            exists={address.token_exists}
                            suspended={address.token_suspended}
                          />
                        </>
                      )}
                    </td>
                    <td data-label={gettext("First seen")}>
                      {timestampToDatetimeString(address.first_seen_at)}
                    </td>
                    <td data-label={gettext("Last seen")}>
                      {timestampToDatetimeString(address.last_seen_at)}
                    </td>
                    <td
                      className="actions"
                      // The row itself opens the user
                      onClick={(e) => e.stopPropagation()}
                    >
                      {address.token_exists && (
                        <Button
                          size="sm"
                          variant={
                            address.token_suspended ? "success" : "warning"
                          }
                          disabled={busy}
                          onClick={() => toggleSuspended(address)}
                        >
                          {address.token_suspended
                            ? gettext("Resume")
                            : gettext("Suspend")}
                        </Button>
                      )}{" "}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => onShowTokens(address.user_id)}
                      >
                        {gettext("All tokens")}
                      </Button>{" "}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() =>
                          onShowActivity({
                            userId: address.user_id,
                            tokenId: address.token_id || undefined,
                          })
                        }
                      >
                        {gettext("Activity")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

interface UserTokensTabProps {
  // Ordered by email
  userChoices: UserChoice[];
  userId?: number;
  setUserId: (userId?: number) => void;
  onShowActivity: (filter: ActivityFilter) => void;
}
/**
 * A user's login sessions and API tokens, for global admins to suspend, resume
 * or delete, e.g. when a token may have leaked
 */
const UserTokensTab: React.FC<UserTokensTabProps> = ({
  userChoices: users,
  userId,
  setUserId,
  onShowActivity,
}: UserTokensTabProps) => {
  const { user: currentUser } = useAuth();
  const [tokens, setTokens] = React.useState<AdminAuthToken[]>();
  const [busy, setBusy] = React.useState<boolean>(false);
  const [reloadCount, setReloadCount] = React.useState<number>(0);
  const loadedUserId = React.useRef<number>();

  React.useEffect(() => {
    let cancelled = false;
    // Another user's tokens are not shown while loading; the same user's stay
    // in place after an action, keeping the focus on the switch just used
    if (loadedUserId.current !== userId) {
      setTokens(undefined);
    }
    loadedUserId.current = userId;
    if (userId === undefined) {
      return;
    }
    apiClient
      .request(`/admin/user-auth-tokens?user_id=${userId}`, { method: "GET" })
      .then((result) => {
        if (!cancelled) {
          setTokens(result.tokens as AdminAuthToken[]);
        }
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          toast.error(gettext("Failed to load tokens"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [userId, reloadCount]);

  const run = async (action: () => Promise<any>, success: string) => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      await action();
      toast.success(success);
    } catch (e) {
      toast.error(gettext((e as any).message.toString()));
    }
    setBusy(false);
    setReloadCount((count) => count + 1);
  };

  // An admin's own web login may be the one they are using
  const confirmOwnLogin = (token: AdminAuthToken) =>
    !token.is_session ||
    userId !== currentUser?.id ||
    window.confirm(
      gettext(
        "This may be your current login. You will be logged out within 5 minutes. Continue?",
      ),
    );

  const setSuspended = (tokenIds: number[], suspended: boolean) =>
    userId !== undefined &&
    run(
      () => setAuthTokensSuspended(userId, tokenIds, suspended),
      suspended ? gettext("Token suspended") : gettext("Token resumed"),
    );

  const deleteToken = (token: AdminAuthToken) => {
    if (
      userId === undefined ||
      !confirmOwnLogin(token) ||
      !window.confirm(
        gettext(
          "Are you sure you want to delete this token? This cannot be undone.",
        ),
      )
    ) {
      return;
    }
    run(
      () =>
        apiClient.request("/admin/delete-auth-tokens", {
          method: "POST",
          data: { user_id: userId, token_ids: [token.id] },
        }),
      gettext("Token deleted"),
    );
  };

  const activeApiTokenIds = (tokens ?? [])
    .filter((token) => !token.is_session && !token.suspended_at)
    .map((token) => token.id);

  return (
    <div>
      <PageHeader title={gettext("Access tokens")} className="mb-3" />
      <Card>
        <Card.Body>
          <p>
            {gettext(
              "A user's web logins and API tokens. A suspended token stops working within 5 minutes and can be resumed at any time.",
            )}
          </p>
          <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
            <Form.Select
              aria-label={gettext("User")}
              value={userId ?? ""}
              onChange={(e: any) =>
                setUserId(e.target.value ? Number(e.target.value) : undefined)
              }
              className="userSelect"
            >
              <option value="">{gettext("Choose a user")}</option>
              {users.map((user) => (
                <option key={`tokens_user_${user.id}`} value={user.id}>
                  {user.display_name
                    ? `${user.display_name} (${user.email})`
                    : user.email}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="warning"
              className="text-nowrap"
              disabled={busy || !activeApiTokenIds.length}
              onClick={() => {
                if (
                  window.confirm(
                    gettext(
                      "Suspend all of this user's active API tokens? Their integrations stop working until the tokens are resumed.",
                    ),
                  )
                ) {
                  setSuspended(activeApiTokenIds, true);
                }
              }}
            >
              {gettext("Suspend all API tokens")}
            </Button>
          </div>
          {userId === undefined ||
          tokens === undefined ? null : !tokens.length ? (
            <div>{gettext("No data available")}</div>
          ) : (
            <Table responsive className="stackedTable">
              <thead>
                <tr>
                  <th>{gettext("Token")}</th>
                  <th>{gettext("Organizations")}</th>
                  <th>{gettext("Last used")}</th>
                  <th>{gettext("Expiry date")}</th>
                  <th>{gettext("Active")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={`admin_token_${token.id}`}>
                    <td data-label={gettext("Token")}>
                      {tokenLabel(
                        token.is_session,
                        token.description,
                        token.id,
                      )}
                      <TokenStateBadges
                        exists
                        suspended={!!token.suspended_at}
                      />
                      {token.suspended_at && (
                        <div className="text-muted small">
                          {timestampToDatetimeString(token.suspended_at)}
                          {token.suspended_by && `, ${token.suspended_by}`}
                        </div>
                      )}
                    </td>
                    <td data-label={gettext("Organizations")}>
                      {token.organizations.length
                        ? token.organizations.map((o) => o.name).join(", ")
                        : token.is_session
                          ? "-"
                          : gettext("All of the user's organizations")}
                    </td>
                    <td data-label={gettext("Last used")}>
                      {token.last_used_at
                        ? timestampToDatetimeString(token.last_used_at)
                        : gettext("Never")}
                      {token.last_used_ip && (
                        <div className="text-muted small">
                          {token.last_used_ip}
                        </div>
                      )}
                    </td>
                    <td data-label={gettext("Expiry date")}>
                      {token.expire_timestamp
                        ? timestampToDatetimeString(token.expire_timestamp)
                        : gettext("Never")}
                    </td>
                    <td data-label={gettext("Active")}>
                      <Form.Check
                        type="switch"
                        id={`admin_token_active_${token.id}`}
                        aria-label={gettext("Active")}
                        checked={!token.suspended_at}
                        disabled={busy}
                        onChange={() =>
                          (token.suspended_at || confirmOwnLogin(token)) &&
                          setSuspended([token.id], !token.suspended_at)
                        }
                      />
                    </td>
                    <td className="actions">
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busy}
                        onClick={() => deleteToken(token)}
                      >
                        {gettext("Delete")}
                      </Button>{" "}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() =>
                          userId !== undefined &&
                          onShowActivity({ userId, tokenId: token.id })
                        }
                      >
                        {gettext("Activity")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

interface ActivityFilter {
  userId?: number;
  tokenId?: number;
}

interface UserActivity {
  id: number;
  occurred_at: string;
  last_at: string;
  count: number;
  user_id: number | null;
  user_email: string | null;
  token_id: number | null;
  token_is_session: boolean | null;
  token_description: string | null;
  ip_address: string | null;
  source: string;
  action: string;
  outcome: "success" | "denied" | "rejected" | "error";
  status: number | null;
  details: Record<string, any> | null;
}

// What the recorded API routes do, in words
const ACTIVITY_LABELS: Record<string, string> = {
  auth_login_user: "Login",
  auth_token_logout: "Logout",
  auth_token_reissue: "Token used",
  auth_token_add: "Token created",
  auth_token_delete: "Token deleted",
  auth_update_profile: "Own profile changed",
  auth_request_recovery_token: "Password recovery requested",
  auth_submit_recovery_token: "Password recovery code entered",
  auth_create_user: "User created",
  auth_update_user: "User changed",
  auth_create_organization: "Organization created",
  auth_update_organization: "Organization changed",
  admin_delete_user: "User deleted",
  admin_request_password_change: "Password change requested by an admin",
  admin_suspend_auth_tokens: "Tokens suspended or resumed",
  admin_delete_auth_tokens: "Tokens deleted by an admin",
  export_csv: "CSV export",
  export_excel: "Excel export",
  bulk_export_csv_async: "Bulk CSV export started",
  bulk_export_excel_async: "Bulk Excel export started",
  bulk_export_download_url: "Bulk export downloaded",
  fileupload_station_file: "Station file downloaded",
  fileupload_upload_station_file: "Station file uploaded",
  offline_report_detail: "Offline report opened",
  burrel_camera_image: "Camera image downloaded",
  auth_editable_users: "User list viewed",
  admin_user_ip_addresses: "Login IP addresses viewed",
  admin_user_auth_tokens: "User's tokens viewed",
};

const OUTCOME_BADGES: Record<UserActivity["outcome"], string> = {
  success: "success",
  denied: "danger",
  rejected: "warning",
  error: "dark",
};

const OUTCOME_LABELS: Record<UserActivity["outcome"], string> = {
  success: "Succeeded",
  denied: "Denied",
  rejected: "Rejected",
  error: "Failed",
};

const PROBLEM_OUTCOMES = "denied,rejected,error";

const describeValue = (value: any) =>
  typeof value === "string" ? value : JSON.stringify(value);

/**
 * A request's recorded details, one per line, e.g. "email: a@b.fi" and
 * 'other fields: ["password"]'. Field names are the sender's to choose, so
 * they are quoted and never run into the other details.
 */
const describeDetails = (details: UserActivity["details"]): string[] => {
  if (!details) {
    return [];
  }
  const parts: string[] = [];
  if (details.error) {
    parts.push(details.error);
  }
  if (details.token_refused) {
    parts.push(`${gettext("Token refused")}: ${details.token_refused}`);
  }
  Object.entries({ ...details.route, ...details.values }).forEach(
    ([key, value]) => parts.push(`${key}: ${describeValue(value)}`),
  );
  if (details.fields?.length) {
    parts.push(`${gettext("other fields")}: ${JSON.stringify(details.fields)}`);
  }
  if (details.new_token_id) {
    parts.push(`${gettext("new token")} #${details.new_token_id}`);
  }
  return parts;
};

const activityLabel = (row: UserActivity) => {
  if (row.source === "mg-ext-api") {
    return `${gettext("Integration API")}: ${row.action}`;
  }
  return ACTIVITY_LABELS[row.action]
    ? gettext(ACTIVITY_LABELS[row.action])
    : row.action;
};

interface UserActivityTabProps {
  // Ordered by email
  userChoices: UserChoice[];
  filter: ActivityFilter;
  setFilter: (filter: ActivityFilter) => void;
}
/**
 * What users and their tokens did: state-changing requests, downloads,
 * logins, token use and the integration API's requests, e.g. to tell an
 * integration downloading data from someone changing accounts with its token
 */
const UserActivityTab: React.FC<UserActivityTabProps> = ({
  userChoices: users,
  filter,
  setFilter,
}: UserActivityTabProps) => {
  const [days, setDays] = React.useState<number>(7);
  const [problemsOnly, setProblemsOnly] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>("");
  const [activity, setActivity] = React.useState<UserActivity[]>();
  const [truncated, setTruncated] = React.useState<boolean>(false);

  React.useEffect(() => {
    let cancelled = false;
    setActivity(undefined);
    const params = new URLSearchParams({ days: String(days) });
    if (filter.userId !== undefined) {
      params.set("user_id", String(filter.userId));
    }
    if (filter.tokenId !== undefined) {
      params.set("token_id", String(filter.tokenId));
    }
    if (problemsOnly) {
      params.set("outcomes", PROBLEM_OUTCOMES);
    }
    apiClient
      .request(`/admin/user-activity?${params}`, { method: "GET" })
      .then((result) => {
        if (!cancelled) {
          setActivity(result.activity as UserActivity[]);
          setTruncated(!!result.truncated);
        }
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          toast.error(gettext("Failed to load activity"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [days, filter.userId, filter.tokenId, problemsOnly]);

  const query = search.trim().toLowerCase();
  const shownActivity = (activity ?? []).filter(
    (row) =>
      !query ||
      [
        activityLabel(row),
        row.action,
        row.user_email ?? "",
        row.ip_address ?? "",
        row.token_description ?? "",
        ...describeDetails(row.details),
      ].some((text) => text.toLowerCase().includes(query)),
  );

  return (
    <div>
      <PageHeader title={gettext("Activity")} className="mb-3" />
      <Card>
        <Card.Body>
          <p>
            {gettext(
              "Changes, downloads, logins and token use by each user, and the requests integrations made through the integration API. Reading data in the web application is not recorded.",
            )}
          </p>
          <div className="d-flex flex-column flex-sm-row flex-sm-wrap gap-2 mb-3 align-items-sm-center">
            <Form.Select
              aria-label={gettext("Period")}
              value={days}
              onChange={(e: any) => setDays(Number(e.target.value))}
              className="periodSelect"
            >
              {Object.entries(IP_ADDRESS_PERIOD_OPTIONS).map(([key, label]) => (
                <option key={`activity_period_${key}`} value={key}>
                  {gettext(label)}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              aria-label={gettext("User")}
              value={filter.userId ?? ""}
              onChange={(e: any) =>
                setFilter({
                  userId: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              className="userSelect"
            >
              <option value="">{gettext("All users")}</option>
              {filter.userId !== undefined &&
                !users.some((user) => user.id === filter.userId) && (
                  // e.g. a deleted account, opened from its addresses
                  <option value={filter.userId}>
                    {`${gettext("User")} #${filter.userId}`}
                  </option>
                )}
              {users.map((user) => (
                <option key={`activity_user_${user.id}`} value={user.id}>
                  {user.display_name
                    ? `${user.display_name} (${user.email})`
                    : user.email}
                </option>
              ))}
            </Form.Select>
            {filter.tokenId !== undefined && (
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setFilter({ userId: filter.userId })}
              >
                {`${gettext("Token")} #${filter.tokenId}`} ✕
              </Button>
            )}
            <Form.Check
              type="switch"
              id="activity_problems_only"
              label={gettext("Only denied, rejected or failed")}
              checked={problemsOnly}
              onChange={() => setProblemsOnly(!problemsOnly)}
            />
            <FormControl
              value={search}
              placeholder={gettext("Search activity")}
              onChange={(e: any) => setSearch(e.target.value)}
              className="searchInput"
            />
          </div>
          {truncated && (
            <p className="text-warning">
              {gettext(
                "Only the most recent activity is shown. Choose a shorter period or a user to narrow the list.",
              )}
            </p>
          )}
          {activity === undefined ? null : !shownActivity.length ? (
            <div>{gettext("No data available")}</div>
          ) : (
            <Table size="sm" responsive className="stackedTable">
              <thead>
                <tr>
                  <th>{gettext("Time")}</th>
                  <th>{gettext("User")}</th>
                  <th>{gettext("Authentication")}</th>
                  <th>{gettext("IP address")}</th>
                  <th>{gettext("Action")}</th>
                  <th>{gettext("Result")}</th>
                  <th>{gettext("Details")}</th>
                </tr>
              </thead>
              <tbody>
                {shownActivity.map((row) => (
                  <tr key={`activity_${row.id}`}>
                    <td data-label={gettext("Time")} className="text-lg-nowrap">
                      {timestampToDatetimeString(row.last_at)}
                      {row.count > 1 && (
                        <div className="text-muted small">
                          {`×${row.count}, ${gettext("first")} ${timestampToDatetimeString(row.occurred_at)}`}
                        </div>
                      )}
                    </td>
                    <td data-label={gettext("User")} className="text-break">
                      {row.user_email ?? "-"}
                    </td>
                    <td data-label={gettext("Authentication")}>
                      {row.token_id
                        ? tokenLabel(
                            row.token_is_session,
                            row.token_description,
                            row.token_id,
                          )
                        : "-"}
                    </td>
                    <td data-label={gettext("IP address")}>
                      {row.ip_address ?? "-"}
                    </td>
                    <td data-label={gettext("Action")}>{activityLabel(row)}</td>
                    <td data-label={gettext("Result")}>
                      <Badge bg={OUTCOME_BADGES[row.outcome]}>
                        {gettext(OUTCOME_LABELS[row.outcome])}
                      </Badge>
                    </td>
                    <td
                      data-label={gettext("Details")}
                      className="small text-break"
                    >
                      {describeDetails(row.details).map((part, index) => (
                        <div key={`activity_${row.id}_detail_${index}`}>
                          {part}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

interface UserManagementTabsProps {
  managedOrganizations: ManagedOrganizationOption[];
  userReloadCount: number;
  userRefetch: () => void;
  permissions?: string[];
}
const UserManagementTabs: React.FC<UserManagementTabsProps> = ({
  managedOrganizations,
  userReloadCount,
  userRefetch,
  permissions = [],
}: UserManagementTabsProps) => {
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.admin;
  const defaultKey = permissions.includes("list_users") ? "list" : "add";
  const [activeKey, setActiveKey] = React.useState<string>(defaultKey);
  const [tokensUserId, setTokensUserId] = React.useState<number>();
  const [activityFilter, setActivityFilter] = React.useState<ActivityFilter>(
    {},
  );
  // Every user, for the tabs that choose one: loaded once one of them is opened
  const [userChoices, setUserChoices] = React.useState<UserChoice[]>();
  const choosesUser = activeKey === "tokens" || activeKey === "activity";
  React.useEffect(() => {
    if (!choosesUser || userChoices !== undefined) {
      return;
    }
    let cancelled = false;
    loadUserChoices()
      .then((choices) => !cancelled && setUserChoices(choices))
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          toast.error(gettext("Failed to load users"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [choosesUser, userChoices]);

  const showActivity = (filter: ActivityFilter) => {
    setActivityFilter(filter);
    setActiveKey("activity");
  };
  return (
    <Tabs
      activeKey={activeKey}
      onSelect={(key) => key && setActiveKey(key)}
      id="user-management-tabs"
      // One row that scrolls sideways on a phone, rather than several
      className="mb-3 flex-nowrap text-nowrap overflow-auto"
    >
      {permissions.includes("list_users") && (
        <Tab eventKey="list" title="Existing Users">
          <UserListTab reloadCount={userReloadCount} />
        </Tab>
      )}
      {isGlobalAdmin && (
        // Loaded only when opened
        <Tab
          eventKey="ipAddresses"
          title={gettext("Login IP addresses")}
          mountOnEnter
        >
          <UserIPAddressesTab
            onShowTokens={(userId) => {
              setTokensUserId(userId);
              setActiveKey("tokens");
            }}
            onShowActivity={showActivity}
          />
        </Tab>
      )}
      {isGlobalAdmin && (
        <Tab eventKey="tokens" title={gettext("Access tokens")} mountOnEnter>
          <UserTokensTab
            userChoices={userChoices ?? []}
            userId={tokensUserId}
            setUserId={setTokensUserId}
            onShowActivity={showActivity}
          />
        </Tab>
      )}
      {isGlobalAdmin && (
        <Tab eventKey="activity" title={gettext("Activity")} mountOnEnter>
          <UserActivityTab
            userChoices={userChoices ?? []}
            filter={activityFilter}
            setFilter={setActivityFilter}
          />
        </Tab>
      )}
      {permissions.includes("add_user") && (
        <Tab eventKey="add" title="Add User">
          <UserAddTab
            managedOrganizations={managedOrganizations}
            userRefetch={userRefetch}
            permissions={permissions}
          />
        </Tab>
      )}
    </Tabs>
  );
};

interface UserManagementPageProps {
  permissions?: string[];
}
const UserManagementPage: React.FC<UserManagementPageProps> = ({
  permissions,
}) => {
  const { user } = useAuth();

  const [managedOrganizations, setManagedOrganizations] = React.useState<
    ManagedOrganizationOption[]
  >([]);
  // Each change loads the list of users again
  const [userReloadCount, setUserReloadCount] = React.useState<number>(0);

  const organizationRefetch = () =>
    loadManagedOrganizations({
      setManagedOrganizations,
    });
  const userRefetch = () => setUserReloadCount((count) => count + 1);

  React.useEffect(() => {
    organizationRefetch();
  }, [setManagedOrganizations]);

  if (!user) {
    return <div>Not logged in</div>;
  }
  return (
    <div className="UserManagementPage">
      <UserManagementTabs
        userReloadCount={userReloadCount}
        userRefetch={userRefetch}
        managedOrganizations={managedOrganizations}
        permissions={permissions}
      />
    </div>
  );
};
export default UserManagementPage;
